import { useMutation } from '@tanstack/react-query';
import { MouseEvent } from 'react';

import { _fundFollow, _fundUnfollow } from '@/api/fund/mutations';
import PlusCircleIcon from '@/assets/svg/ui-elements/plus-circle-outline.svg';
import { Button } from '@/design-system';

interface FollowFundButtonProps {
  fundId: string;
  fundName: string;
  isFollowed: boolean;
  fullWidth?: boolean;
}

export const FollowFundButton = ({
  fundId,
  fundName,
  isFollowed,
  fullWidth,
}: FollowFundButtonProps) => {
  const { mutate: followFund, isPending: isFollowPending } = useMutation({
    mutationFn: _fundFollow,
  });
  const { mutate: unfollowFund, isPending: isUnfollowPending } = useMutation({
    mutationFn: _fundUnfollow,
  });

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (isFollowed) {
      unfollowFund({ fundId });
      return;
    }

    followFund({ fundId });
  };

  if (isFollowed) {
    return (
      <Button
        size="sm"
        variant="outline"
        color="tertiary"
        fullWidth={fullWidth}
        disabled={isUnfollowPending}
        aria-label={`Unfollow ${fundName}`}
        onClick={handleClick}
      >
        Unfollow
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      variant="subtle"
      color="secondary"
      fullWidth={fullWidth}
      disabled={isFollowPending}
      aria-label={`Follow ${fundName}`}
      onClick={handleClick}
    >
      <PlusCircleIcon width={16} height={16} /> Follow
    </Button>
  );
};
